/**
 * Which live polls a participant still has to answer, and in what order.
 *
 * A poll raised while you are sitting in the meeting interrupts you, and that
 * has always worked — `poll:created` arrives on the channel and the voting
 * dialog opens. Someone who joins AFTER the question was asked got nothing:
 * their polls arrive through the catch-up fetch, which deliberately never
 * prompted, so the poll sat in the Polls panel waiting to be found. A
 * participant who never thought to open Apps never voted.
 *
 * That silence was a considered choice — the reasoning in `MeetingScreen` was
 * that being met on arrival by a question asked before you got there is an
 * interruption rather than a prompt. It is being reversed deliberately: a live
 * poll is a question the room is waiting on, and a participant who cannot see
 * it cannot answer it.
 *
 * This module decides WHAT to ask and in WHICH ORDER. The showing of one dialog
 * after another is wiring, and lives in the hook.
 *
 * ## The three exclusions, and why each is not optional
 *
 * **Closed polls.** Answering a poll the host has already ended does nothing,
 * and presenting it implies the room is still waiting.
 *
 * **Polls you wrote.** The author already has the question in front of them in
 * the panel; prompting them with their own is noise, and it would fire the
 * moment they created it. Matched two ways: `authoredPollIds` (raised from this
 * tab) and the poll's own `createdById`. The id list alone lost a race — the
 * server broadcasts `poll:created` before it answers the create request, so the
 * author's own broadcast often arrived before the id was recorded and the
 * creator was prompted to vote on their own question.
 *
 * **Polls you have answered.** Includes the reload case — a participant who
 * voted and then refreshed must not be asked again, and their vote is the only
 * record that they were.
 *
 * **Polls already shown to you.** Marked when the prompt OPENS, not when it is
 * answered, which is what makes dismissing stick: closing the dialog does not
 * make a poll un-promptable by itself, so without this the same question
 * reopened the instant it was closed. It also removes the race on voting, where
 * the vote is recorded only after a network round trip while the dialog closes
 * immediately — for that moment the poll still looks unanswered. See
 * `poll-prompt-memory`.
 *
 * Order is NEWEST first, by `createdAt`. The most recent question is the one
 * the room is most likely still waiting on, and someone who answers only the
 * first prompt and closes the rest has answered the one that mattered most. A
 * deterministic order also means a re-render cannot reshuffle what is on
 * screen.
 */

export type PromptablePoll = {
  id: string
  isActive: boolean
  /** Epoch millis. Used only for ordering. */
  createdAt: number
  /** The creator's user id, which is their peer id — see `isOwnPoll`. */
  createdById?: string
}

export type PromptableVote = {
  pollId: string
  participantId: string
}

export type PollPromptInput = {
  polls: readonly PromptablePoll[]
  /** Every vote this client knows about, from every participant. */
  votes: readonly PromptableVote[]
  /** Polls this client created. */
  authoredPollIds: readonly string[]
  /** Polls already put in front of this participant — see `poll-prompt-memory`. */
  promptedPollIds: readonly string[]
  /** Whose votes count as "already answered". */
  localParticipantId: string | null | undefined
}

/**
 * Whether this participant created the poll, by the creator id the server
 * stamps on it. The creator is never asked to vote on their own poll, and the
 * server refuses the vote if they try.
 */
export function isOwnPoll(
  poll: { createdById?: string } | null | undefined,
  localParticipantId: string | null | undefined,
): boolean {
  return Boolean(poll?.createdById && localParticipantId && poll.createdById === localParticipantId)
}

/**
 * The polls to put in front of this participant, newest first.
 *
 * Returns ids rather than polls so the caller holds one source of truth for the
 * poll itself — a queue of copies would go stale the moment a vote landed and
 * the tallies moved.
 */
export function pollsToPrompt({
  polls,
  votes,
  authoredPollIds,
  promptedPollIds,
  localParticipantId,
}: PollPromptInput): string[] {
  // No identity means no way to tell an answered poll from an unanswered one.
  // Prompting anyway would ask a participant to vote a second time; the roster
  // resolves a moment later and the prompt arrives then.
  if (!localParticipantId) return []

  const authored = new Set(authoredPollIds)
  const shown = new Set(promptedPollIds)
  const answered = new Set(
    votes.filter((v) => v.participantId === localParticipantId).map((v) => v.pollId),
  )

  return polls
    // `filter` already returns a new array, so the `sort` below cannot reach
    // the caller's. A defensive `slice()` sat here until mutation testing
    // showed it could be deleted without failing anything.
    .filter(
      (p) =>
        p.isActive &&
        !authored.has(p.id) &&
        !isOwnPoll(p, localParticipantId) &&
        !answered.has(p.id) &&
        !shown.has(p.id),
    )
    .sort((a, b) => b.createdAt - a.createdAt)
    .map((p) => p.id)
}

/**
 * Fold a fresh decision into the queue that is already on screen.
 *
 * Appends what is newly promptable and drops what no longer is — a poll the
 * host ended, or one just voted on — WITHOUT reordering or re-adding anything
 * already queued. Recomputing the queue outright on every render would be
 * simpler and wrong: the participant is looking at its head, and a poll landing
 * or a tally moving would swap the question under the pointer.
 */
export function reconcileQueue(
  queue: readonly string[],
  promptable: readonly string[],
): string[] {
  const live = new Set(promptable)
  const kept = queue.filter((id) => live.has(id))
  const known = new Set(kept)
  // Appended in `promptable` order, which is newest first.
  return [...kept, ...promptable.filter((id) => !known.has(id))]
}

/**
 * Take the next question to ask, and mark it asked — in one step.
 *
 * These two have to happen together. Raising a poll without recording it leaves
 * it qualified, so closing the dialog re-raises the same question; recording it
 * without removing it from the queue leaves the head in place for a render, so
 * a second can arrive on top of the first. Both were live bugs, and both came
 * of doing one half in the component and the other somewhere else.
 *
 * Returning the whole next state rather than mutating anything means the caller
 * has nothing left to get wrong: there is no order to observe and no step to
 * forget.
 */
export function takeNextPrompt(
  queue: readonly string[],
  shown: readonly string[],
): { next: string | null; queue: string[]; shown: string[] } {
  const next = queue[0] ?? null
  if (next === null) return { next: null, queue: [...queue], shown: [...shown] }

  return {
    next,
    queue: queue.slice(1),
    shown: shown.includes(next) ? [...shown] : [...shown, next],
  }
}
